import { useState, useEffect, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    Button,
    Switch,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import { EditRounded } from "@mui/icons-material";

import {
    getAllVenueApi,
    updateVenueApi,
    getVenueByCityIdApi,
} from "../../../../src/redux/api/api";

const Venue = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [venue, setVenue] = useState([]);
    const [selectedState, setSelectedState] = useState(null);

    const navigate = useNavigate();
    const { state } = useLocation();

    // 🔹 Fetch all venues
    const getAllVenue = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await getAllVenueApi();
            if (res.status === 200) {
                setVenue(res.data.info);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch venues");
        } finally {
            setIsLoading(false);
        }
    }, []);

    // 🔹 Fetch venues by city
    const getVenueByCityId = useCallback(async (cityId) => {
        setIsLoading(true);
        try {
            const res = await getVenueByCityIdApi({ cityid: cityId });
            if (res.status === 200) {
                setVenue(res.data.info);
            }
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.message || "Failed to fetch venues");
        } finally {
            setIsLoading(false);
        }
    }, []);

    // 🔹 Update venue status
    const handleChangeStatus = async (data) => {
        try {
            const res = await updateVenueApi(data);
            if (res.status === 200) {
                setVenue((prev) =>
                    prev.map((item) =>
                        item.id === data.id
                            ? { ...item, status: res.data.info.status }
                            : item
                    )
                );
                toast.success("Status updated successfully");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to update status");
        }
    };

    // 🔹 Load data on mount
    useEffect(() => {
        if (state) {
            getVenueByCityId(state);
            setSelectedState(state);
        } else {
            getAllVenue();
        }
    }, [state, getAllVenue, getVenueByCityId]);

    // 🔹 Columns for DataGrid
    const columns = [
        { field: "address", headerName: "Address", flex: 1 },
        { field: "cityname", headerName: "City", flex: 1 },
        { field: "statename", headerName: "State", flex: 1 },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => (
                <Switch
                    color="warning"
                    checked={!!params.row.status}
                    onChange={() =>
                        handleChangeStatus({ id: params.row.id, status: !params.row.status })
                    }
                />
            ),
        },
        {
            field: "action",
            headerName: "Action",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button
                    onClick={() => navigate("/venue/form", { state: params.row })}
                    variant="outlined"
                    startIcon={<EditRounded />}
                >
                    Edit
                </Button>
            ),
        },
    ];

    return (
        <Box p={3}>
            {isLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                    >
                        <Typography variant="h5">Venues</Typography>
                        <Stack direction="row" spacing={2}>
                            {selectedState && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => {
                                        setSelectedState(null);
                                        getAllVenue();
                                    }}
                                >
                                    All Venues
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={() => navigate("/venue/form")}
                            >
                                Add Venue
                            </Button>
                        </Stack>
                    </Stack>

                    <DataGrid
                        rows={venue}
                        columns={columns}
                        autoHeight
                        pageSize={10}
                        rowsPerPageOptions={[5, 10, 20]}
                        disableSelectionOnClick
                        getRowId={(row) => row.id}
                    />
                </>
            )}
            <ToastContainer />
        </Box>
    );
};

export default Venue;
